/**
 * A tiny path router.
 *
 *   /                     Overview (every group at a glance)
 *   /setup                setting yourself up: full name, then which groups and tutoring are you
 *   /everyone             People: one row per person across every group and Tutoring
 *   /you                  Your settings (account, devices, groups)
 *   /admin                Accounts: everyone with an account, join requests (treasurer)
 *   /group/:name/:page    a group's pages by its readable name (like /group/costco-club/people):
 *                         ledger (default), overview, people, payments, receipts, history,
 *                         settings, more (and ask). The group's Overview page was called Insights,
 *                         and /insights links still open it. Older /g/:groupId/:page links still work.
 *   /p/:token             an invite link
 *   /k/:token             a passkey link: make a new passkey for your account
 *
 * Older single-group paths (/people, /trips, /settings, ...) still work: they open that page
 * in the group you had open last.
 */

export const GROUP_PAGES = [
  "ledger",
  "people",
  "payments",
  "trips",
  "insights",
  "history",
  "ask",
  "settings",
  "more",
] as const;
export const TUTORING_TABS = ["students", "calendar", "tutors", "packages"] as const;
export type TutoringTab = (typeof TUTORING_TABS)[number];

export type GroupPage = (typeof GROUP_PAGES)[number];

export type Route =
  | { name: "overview" }
  | { name: "you" }
  | { name: "people" }
  | { name: "setup" }
  | { name: "admin" }
  | { name: "roadmap" }
  | { name: "money" }
  | { name: "tutoring"; tab: TutoringTab }
  | { name: "group"; page: GroupPage; groupId: string | null }
  | { name: "person-view"; token: string }
  | { name: "passkey-link"; token: string };

const SLUG: Record<string, GroupPage> = {
  "": "ledger",
  ledger: "ledger",
  people: "people",
  payments: "payments",
  receipts: "trips",
  trips: "trips",
  insights: "insights",
  overview: "insights",
  history: "history",
  ask: "ask",
  settings: "settings",
  more: "more",
};

// ---- readable names for groups in links: "Costco Club" -> costco-club (unique per account)
let slugById = new Map<string, string>();
let idBySlug = new Map<string, string>();

export function slugify(name: string): string {
  return (
    name
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/['’]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "group"
  );
}

/** Gives every group a readable, unique link name. Called whenever the list of groups changes. */
export function setGroupSlugs(groups: readonly { id: string; name: string }[]): void {
  const byId = new Map<string, string>();
  const byS = new Map<string, string>();
  for (const g of groups) {
    if (byId.has(g.id)) continue;
    const base = slugify(g.name);
    let s = base;
    for (let n = 2; byS.has(s); n++) s = `${base}-${n}`;
    byId.set(g.id, s);
    byS.set(s, g.id);
  }
  slugById = byId;
  idBySlug = byS;
}

export function parse(path: string): Route {
  const p = path.split(/[?#]/)[0]!.replace(/\/+$/, "") || "/";
  const view = /^\/p\/([A-Za-z0-9_-]{20,})$/.exec(p);
  if (view) return { name: "person-view", token: view[1]! };
  const key = /^\/k\/([A-Za-z0-9_-]{20,})$/.exec(p);
  if (key) return { name: "passkey-link", token: key[1]! };
  if (p === "/") return { name: "overview" };
  if (p === "/you") return { name: "you" };
  if (p === "/everyone") return { name: "people" };
  if (p === "/setup") return { name: "setup" };
  if (p === "/admin") return { name: "admin" };
  if (p === "/roadmap") return { name: "roadmap" };
  if (p === "/money") return { name: "money" };
  const tut = /^\/tutoring(?:\/([a-z]+))?$/.exec(p);
  if (tut) {
    const tab = (TUTORING_TABS as readonly string[]).includes(tut[1] ?? "")
      ? (tut[1] as TutoringTab)
      : "students";
    return { name: "tutoring", tab };
  }
  const named = /^\/group\/([^/]+)(?:\/([a-z]+))?$/.exec(p);
  if (named) {
    const slug = decodeURIComponent(named[1]!);
    // Before the groups have loaded the name can't be matched yet; "" means "not known yet".
    return {
      name: "group",
      groupId: idBySlug.get(slug) ?? "",
      page: SLUG[named[2] ?? ""] ?? "ledger",
    };
  }
  const g = /^\/g\/([^/]+)(?:\/([a-z]+))?$/.exec(p);
  if (g)
    return {
      name: "group",
      groupId: decodeURIComponent(g[1]!),
      page: SLUG[g[2] ?? ""] ?? "ledger",
    };
  const legacy = SLUG[p.slice(1)];
  if (legacy) return { name: "group", groupId: null, page: legacy };
  return { name: "overview" };
}

/** The path to a page of a group. */
export function groupPath(groupId: string, page: GroupPage = "ledger"): string {
  const slug = page === "trips" ? "receipts" : page === "insights" ? "overview" : page;
  const tail = slug === "ledger" ? "" : `/${slug}`;
  const name = slugById.get(groupId);
  return name ? `/group/${name}${tail}` : `/g/${encodeURIComponent(groupId)}${tail}`;
}
